/**
 * @module geo
 */
import { Geometry } from 'geojson';
import { Coordinate, Feature } from 'openlayers'
import { applyMiddleware, compose, createStore, Middleware, Store } from 'redux';
import { createEpicMiddleware, Options } from 'redux-observable';
import watch from 'redux-watch';
import { Observable } from 'rxjs/Observable';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { of } from 'rxjs/observable/of';
import { map, pluck ,first} from 'rxjs/operators';

import filter from 'lodash.filter';

import { dispatchDataRefreshEventCreatorP, initApplicationCreatorP, setBaseLayerVisibilityCreatorP, setComponentsStateCreatorP, setFilterHistoryTimestampCreatorP, setGlobalParameterValueCreatorP, ApplicationAvailableCreator, clearServiceCreator } from './actions/creators';
import { registerExtensionCreatorP } from './actions/creators/register.extension.creator';
import { TypeKeyUtils } from './actions/types.key.utils';
import { BaseLayerObj, HashBaseLayer, HashBaseLayerVisibility } from './base.layer';
import busService from './bus';
import { Component, ComponentInstanceLoader, HashComponents, ComponentsStore } from './components';
import { IframeContainer } from './container';
import { Watch } from './decorators/watch';
import { FunctionalityDeserializer } from './Deserializer';
import { Functionality as functionalityObj, HashFunctionalities } from './functionalities';
import { FunctionalityService } from './functionalities/services';
import { Map } from './map';
import { epics } from './middleware';
import { INIT_APP, REGISTER_EXTENSION, SET_GLOBAL_PARAMETER_VALUE } from './payload/facade';
import { BaseLayersVisilityPayload } from './payload/facade/SET_BASELAYERS_VISIBILITIES';
import { UTIL_CONVERT_PROJ } from './payload/facade/UTIL_CONVERT_PROJ';
import { ConvertProjDone } from './payload/response/convert.proj.done';
import { ProviderApp } from './provider.app';
import { mainReducer } from './reducers/app';
import { Selection } from './selection';
import { IHashServices, ServiceLoader, ServiceTypes } from './services/service.loader';
import { AppStore } from './store/app';
import { appInitialized } from './store/selectors';
import { BaseLayer } from './types/base.layer';
import { ExtensionProperties, ExtensionType } from './types/extensions';
import { GeoEvent } from './types/geo.event';
import { MapStore } from './types/map';
import { Watcher } from './watcher';
import { ApplicationOptions } from './types/application.options';
import { IHashComponents } from './types/component';
import { IHashServicesOperations } from './types/services';



/**
 * This is the main object exposed by the api, referencing all other objects and
 * classes
 *
 *
 * From the instance, you can listen to all properties of containers,
 * functionalities,and maps, and manage them.
 *
 * An application is built from [[ApplicationOptions]]
 *      , which handle all
 * parameters at initialization, for instance
 *
 * ```javascript
 *
 * var initState = {
 *    map: {
 *        position: {
 *            coordinates: [3, 47],
 *            crs: "EPSG:4326"
 *        },
 *        extent: { // emprise dans la projection souhaitée
 *            minX: 5.76,
 *            maxX: 5.79,
 *            minY: 45.82,
 *            maxY: 45.84,
 *            crs: "EPSG:4326"
 *        },
 *        entries: [
 *            { key: "d9d70c9a-c3ac-11e7-972e-8bf4e374d5f6", visible: false },
 *            { name: "communes", visible: false },
 *        ],
 *        markers: [
 *            {
 *                position: {
 *                    coordinates: [3, 47],
 *                    crs: "EPSG:4326"
 *                },
 *                id: 'a marker',
 *                tooltip: {
 *                    title: 'Info title',
 *                    content: '<p> a content</p>',
 *                    footer: '',
 *                    open: true,
 *                    width: 100
 *                },
 *                imageUrl: 'https://geoapi.business-geografic.com/docs/img/marker.png',
 *                size: { h: 34 }
 *            }
 *        ]
 *    },
 *    baseLayers: [
 *        { id: "05786cbc-a4ea-11e7-aa2b-551e58f513b5", visible: true, opacity: 0.8 },
 *        { name: "IGN SCANEXPRESS", visible: false, opacity: 1 }
 *        ],
 *        components: {
 *             'SidePanel': {visible: false},
 *             'SearchBox': {
 *                   visible: true,
 *                   properties: {
 *                         active: {searchesNames: ["Search countries"]}
 *                     }
 *               }
 *        },
 *        functionalities: [
 *            {
 *                name: "towns",
 *                startUp: true,
 *                parameters: {
 *                    filters: [{attribute: "name_city", values: ["Paris"]}]
 *                }
 *            }
 *        ],
 *        theme:
 *            {
 *                 extraCss: "http:exemple/demo/extracss.css",
 *                themeId: '2958dd41-cddf-11e7-8a40-654ed8430b0f'
 *            },
 *        history:{timestamp:'2018-10-10'}
 *    }
 *
 *
 *
 * var geoapp = new GeoApp(idElement, urlApplication, initState);
 * geoapp.render().then(function(){
 *  // do something or not
 * });
 *
 * ```
 *
 */
export interface Application extends Watcher { }



@Watch({})
export class Application implements ApplicationOptions {
  /**
   * Unique identifier of app
   */
  public uid: any;

  public path: string = '';
  /**
   * Div id where to render GEO App
   */
  public elementId: string;
  /**
   * GEO Application url
   */
  public applicationId: string;
  /**
   * true if  Application  is initialized
   */
  public initialized: boolean = false;
  /**
   * Store of the app, see [https://redux.js.org/api-reference/store]
   */
  public store: Store<AppStore>;
  // /**
  //  * Store referencing user events
  //  */
  // public storeUnstable: Store<EventsStore>;
  /**
   * A reference to the Iframe container
   */
  public container: IframeContainer;
  /**
   * list of observers binded
   * @private
   */
  public observers: any;
  /**
   * The DOM element containing the view.
   */
  public element: HTMLElement | null = null;
  /**
   * Bus Object to communicate with proxy
   * blabla
   */
  public bustest: any;
  public bus: any;
  public events?: GeoEvent;
  // /**
  //  * current features on map
  //  */
  // get features(): Feature[] {
  //   return this._selection.features;
  // }

  protected _selection: Selection;

  public version = 'process.env.VERSION';
  protected _initApp: INIT_APP;
  protected _functionalities: HashFunctionalities | null;

  private _baseLayers: HashBaseLayer = {};

  public components: HashComponents = {};

  protected _map: Map | null = null;

  /**
   * List of callable services on this application.
   */
  public Services: IHashServices<any> = {};
  /**
   * @state
   */
  public servicesOperations: IHashServicesOperations = {};

  /**
   * Map object exhibiting GEO map functionality.
   * Use it to manipulate the state of the application's main map (get entries, pan, zoom ...)
   * @state
   */
  get map(): Map | null {
    return this._map;
  }

  /**
   * GeoSelection object
   * It is object to use to manage selection (add or remove features, reset ...)
   * @state
   */
  get selection(): Selection | null {
    return this._selection;
  }
  /**
   * GeoApp constructor. Init application with url and all parameters.
   * @param idElementArg id of div where to render GEO App
   * @param url GEO App url
   * @param init init parameters
   */
  constructor(init: INIT_APP) {
    this.elementId = init.elementId;
    this._initApp = init;
    this.applicationId = init.applicationId;
    const bus = busService;
    this.bus = bus;
    this._functionalities = {};
    this.observers = {};
    this._map = null;
    this._selection = new Selection();

    const uuid: any = (len: number | undefined) => {
      len = len || 7;
      return Math.random().toString(35).substr(2, len);
    };
    this.uid = uuid();

    const defaultConf = { bus: { LOG_LEVEL: 'error' }, geo: { domain: '*' } };

    const config = init.config || defaultConf;
    bus.init(config.bus);
    bus.registerNewApp(this.uid, config);

    const epicMiddleware =
      createEpicMiddleware(epics, { dependencies: this.uid } as Options) as
      Middleware;
    const composeEnhancers =
      (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
    const enhancer = composeEnhancers(applyMiddleware(epicMiddleware));
    const defaultOptions: AppStore = {
      applicationInitialized: false,
      functionalities: {},
      initialized:false,
      iframeInitialized: false,
      map: {},
      uid: this.uid,
      servicesOperations: {}
    };

    this.store =
      createStore(mainReducer, defaultOptions, enhancer) as Store<AppStore>;

    //connect store to instances
    this._selection.connect(this.store);

    // wait for returning app to connect
    ProviderApp(this.store, this);
    this.container =
      new IframeContainer(this.elementId, this.applicationId, bus, this.uid);
    this.handleErrorsLogs();
    this.constructServicesInstances();
    // this.serviceBus = new ServiceBus(this.uid, this.bus);
  }

  /**
   * return the version number of api.
   * @returns {string | undefined}
   */
  public getVersion(): string | undefined {
    return this.version;
  }

  /**
   * linking app and sub class to the store
   * @private
   * @param {AppStore} app
   * @param {Store<AppStore>} store
   */
  public skeletonBuild(app: AppStore, store: Store<AppStore>) {
    this._map = new Map(app.map as MapStore);
    this._map.setApplication(this);
    this._map.connect(store);
  
    const _functionalities: HashFunctionalities = {};
    if (app.functionalities !== null) {
      for (const funcId of Object.keys(app.functionalities)) {
        try {
          _functionalities[funcId] = FunctionalityDeserializer.build(app.functionalities[funcId]);
          _functionalities[funcId].connect(store);
        } catch (e) {
          console.warn(e);
        }
      }
    }

    this._functionalities = _functionalities;

    const baseLayers: HashBaseLayer = {};
    if (app.baseLayers) {
      for (const bl of app.baseLayers) {
        baseLayers[bl.id] = new BaseLayerObj(bl, store);
      }
    }
    this._baseLayers = baseLayers;
    this.connect(store);
    store.dispatch(ApplicationAvailableCreator({status:true}));

  }

  protected constructServicesInstances() {
    const options = { store: this.store };
    for (const i in ServiceTypes) {
      if (ServiceTypes.hasOwnProperty(i)) {
        this.Services[i] =
          ServiceLoader.getServiceInstance(i, this.uid, options);
       
      }
    }
  }
  /**
   * An utility function, to ensure that the function passed as a parameter will only be executed when the application is loaded
   * Strictly equivalent to
   * 
   * ```javascript
   *  let waitInitializater = application.observable('initialized','BehaviourSubject').pipe(
   *  first(initialized => initialized === true)
   * );
   * waitInitializater.subscribe(init =>{
   *  doSomething();
   *
   *  } )
   * ```
   * 
   * @param func Function to execute
   */
  public executeWhenInitialized(func:Function) {
    
    let waitInitializater = this.observable('initialized','BehaviourSubject').pipe(
      first(initialized => initialized === true)
    );
    waitInitializater.subscribe(init =>func())
    return  waitInitializater;

  }
  /**
   * connect to store
   * @private
   * @param store
   */
  public connect(store: Store<AppStore>) {
    const wevents = watch(store.getState, 'eventBus');
    store.subscribe(
      wevents((newVal: GeoEvent, oldVal: GeoEvent, objectPath: string) => {
        this.events = newVal;
      }));

    const wBl = watch(store.getState, 'baseLayers');
    store.subscribe(wBl((newVal: BaseLayer[], oldVal: BaseLayer[]) => {
      const baseLayers: HashBaseLayer = {};
      if (newVal) {
        for (const bl of newVal) {
          baseLayers[bl.id] = new BaseLayerObj(bl, store);
        }
      }
      this._baseLayers = baseLayers;
    }));

    const wComp = watch(store.getState, 'components');
    store.subscribe(
      wComp((newVal: IHashComponents) => {
        const components: HashComponents = {};
        if (newVal) {
          for (const cidx in newVal) {
            if (newVal.hasOwnProperty(cidx)) {
              const comp = newVal[cidx];
              let compObj: Component;

              if (this.components[cidx] instanceof Component) {
                compObj = this.components[cidx];
                Object.assign(compObj, newVal[cidx]);
              } else {
                try {
                  compObj = ComponentInstanceLoader.getInstance(cidx, this.uid, comp);
                  compObj.connect(store);
                } catch (e) {
                  console.warn(e);
                  continue;
                }
              }

              components[cidx] = compObj;
            }
          }
        }
        this.components = components;
      }));
  }

  /**
   * Get all baselayers matching with filter.
   * ```
   * let layers
   * =geoApp.getBaseLayersBy({type:'com.bg.functionality.data.TileWebource'})
   * for(let layer in layers){
   *   ...
   * }
   * @param  filterAttribute
   */
  public getBaseLayersBy(filterAttribute: object): BaseLayerObj[] {
    const values = Object.values(this._baseLayers);
    //  const result: BaseLayerObj[] = filter(values, filterAttribute);
    const result: BaseLayerObj[] = filter(values, filterAttribute);

    return result;
  }

  /**
   * Get a functionality object given by id
   * @param id id of functionality resource
   */
  public getFunctionalityById(id: string): functionalityObj {
    if (this._functionalities && this._functionalities['_' + id] !== null) {
      return this._functionalities[id];
    }
    throw new Error('unknown functionality id ' + id);
  }

  /**
   * Get all functionalities with given name. Returns a list of functionality
   * objects. Alias of getFunctionalityBy({name:name});
   * @see GeoApp#getFunctionalityBy
   * @param name
   */
  public getFunctionalityByName(name: string): functionalityObj[] {
    return this.getFunctionalityBy({ name });
  }

  /**
   * Get all functionalities witch match the filter. Returns a list of
   * functionality objects.
   *
   * ```
   * let functionalities =geoApp.getFunctionalityBy({type:'search'})
   * for(var aFunc in functionalities){
   *   ...
   * }
   * ```
   * @param filterAttribute the filter object of type {attribute:value}
   */
  public getFunctionalityBy(filterAttribute: object): functionalityObj[] {
    if (this._functionalities !== null) {
      const values = Object.values(this._functionalities);
      const result: functionalityObj[] = filter(values, filterAttribute);
      return result;
    } else {
      return [];
    }
  }

  /**
   * Get a resource object given by id
   * @param id id of resource
   * @since v2.0.2
   */
  public getResourceById(id: string): any {
    const resources = this.store.getState().resources;
    if (resources[id] !== null) {
      return resources[id];
    }
    throw new Error('unknown resource id ' + id);
  }

  /**
   * Get all resources with given name. Returns a list of resource objects.
   * Alias of getResourcesBy({name:name});
   * @see GeoApp#getResourceBy
   * @param name
   * @since v2.0.2
   */
  public getResourcesByName(name: string): any[] {
    return this.getResourcesBy({ name });
  }

  /**
   * Get all resources witch match the filter. Returns a list of resource
   * objects.
   *
   * ```
   * let resources = geoApp.getResourceBy({type:'search'})
   * for(var aRes in resources){
   *   ...
   * }
   * ```
   * @param filterAttribute the filter object of type {attribute:value}
   * @since v2.0.2
   */
  public getResourcesBy(filterAttribute: object): any[] {
    const resources = this.store.getState().resources;
    if (resources !== null) {
      const values = Object.values(resources);
      const result: any[] = filter(values, filterAttribute);
      return result;
    } else {
      return [];
    }
  }

  /**
   * Get a baseLayer object by Id
   * @param id
   */
  public getBaseLayerById(id: string): BaseLayerObj | null {
    const bl = this.getBaseLayersBy({ id });
    if (bl) {
      return bl[0];
    }
    throw new Error('unknown baselayer id ' + id);
  }

  /**
   * Get all baselayers matching the name
   * @param  name
   */
  public getBaseLayersByName(name: string): BaseLayerObj[] | null {
    return this.getBaseLayersBy({ name });
  }

  /**
   * Update the visibility of several base layers
   * @since v 2.0.1
   * @param ent list of visibilities {id: true|false,id2: true|false}
   * @return Observable<any>
   */
  public setBaseLayersVisibilities(ent: HashBaseLayerVisibility):
    Observable<any> {
    const ke: BaseLayersVisilityPayload[] = [];
    for (const keyIdx of Object.keys(ent)) {
      const visibility = ent[keyIdx];
      ke.push({ id: keyIdx, visible: visibility });
    }
    return fromPromise(
      setBaseLayerVisibilityCreatorP({ baseLayers: ke }, this.store.dispatch));
  }

  /**
   * Define new value for global parameters
   *
   *
   * ```
   * geoApp.getResourcesBy({'@type':'com.bg.functionality.filtering.GlobalParameter'});
   *
   * geoApp.setGlobalParameterValue({'name':'Département','values':'16'});
   * geoApp.setGlobalParameterValue({'id':"7c88c22e-bb13-11e8-9efd-f3a41702c499",'values':['16','17']});
   * ```
   *
   * @since v 2.0.2
   * @param param
   * @return Observable<any>
   */
  public setGlobalParameterValue(param: SET_GLOBAL_PARAMETER_VALUE):
    Observable<any> {
    return fromPromise(
      setGlobalParameterValueCreatorP(param, this.store.dispatch));
  }
  /**
   * Define new timestamp for history filter
   *
   * ```
   * geoApp.setCurrentTimestamp(Date.now());
   *
   * ```
   *
   * @since v 2.1.0
   * @param timestamp
   * @return Observable<any>
   */
  public setCurrentTimestamp(timestamp: Date | number | null): Observable<any> {
    return fromPromise(
      setFilterHistoryTimestampCreatorP({ timestamp }, this.store.dispatch));
  }

  /**
   * Update the visibility of several components
   * @since v 2.1.0
   * @param components
   * @return Observable<any>
   */
  public setComponentsVisibilities(components: IHashComponents):
    Observable<any> {
    return fromPromise(
      setComponentsStateCreatorP({ components }, this.store.dispatch));
  }

  /**
   * Dispatch event to refresh all components linked to the specified sources
   * (maps, filters...) Call it to say to app when datasource have changed
   *
   *
   * ```
   * var srcs =
   * geoApp.getResourcesBy({'@type':'com.bg.functionality.data.SpatialDataSource','name':'observations'})
   * geoApp.dispatchDataRefreshEvent([srcs[0].id]);
   * ```
   *
   * @param sources
   */
  public dispatchDataRefreshEvent(sources: string[]) {
    return fromPromise(dispatchDataRefreshEventCreatorP(
      { sourceIds: sources }, this.store.dispatch));
  }

  /**
   * get lowlevel service accessor on acf  for functionality
   */
  public getServiceByFunctionality(func: string | functionalityObj):
    FunctionalityService<any, any> | void | unknown {
    const funcObj: functionalityObj = ('string' === typeof func) ?
      this.getFunctionalityById(func) as functionalityObj :
      func as functionalityObj;
    return funcObj.getService();
  }

  /**
   * transform coordinates of geometry from spatial reference to another
   * if "in" or "out" Crs is not defined or null, the current map crs is used when available.
   * 
   * Examples: 
   * 
   * ```js
   * // GeoJSON
   *  var g1 = { type: 'Point', coordinates: [2, 48] };
   *  geoApp.transform(g1, 'EPSG:4326','EPSG:2154').subscribe(function(geom){
   * // do something...
   * })
   * 
   * // coordinates
   * var c1 = [2.5, 48];
   * geoApp.transform(c1 , 'EPSG:4326').subscribe(function(geom){
   * // do something...
   * })
   * 
   * // feature
   * searchFunc.getService().executeSearch({
   *                 "featureId": depFid,
   *                 'centroid': false
   *             }).subscribe(function (vals) {
   * 
   *              geoApp.transform(vals.features[0], null,'EPSG:4326').subscribe(function(geom){
   *                    // do something...
   *              });
   * })
   * 
   * ```
   * 
   * 
   */
  public transform(geometry: Geometry | Coordinate | Feature, inCrs?: number | string | null, outCrs?: number | string): Observable<Geometry | Coordinate | Feature> {
    if (!inCrs) {
      if (this.map !== null && this.map.crs) {
        inCrs = this.map.crs;
      } else {
        throw Error('No in Crs defined');
      }
    }
    if (!outCrs) {
      if (this.map !== null && this.map.crs) {
        outCrs = this.map.crs;
      } else {
        throw Error('No out Crs defined');
      }
    }
    if (inCrs === outCrs) {
      // no transformation
      return of(geometry);
    }
    const payload = new UTIL_CONVERT_PROJ({
      in: { geometry, crs: inCrs },
      out: { crs: outCrs }
    });
    return fromPromise(
      this.bus.geoBus.send(
        TypeKeyUtils.UTIL_CONVERT_PROJ,
        payload, this.uid)
    ).pipe(
      map((res: any) => {
        return res.data as ConvertProjDone;
      }),
      pluck('out', 'geometry')
    );


  }

  public registerExtension<E extends ExtensionProperties>(type: ExtensionType, properties: E) {
    const extension = new REGISTER_EXTENSION(type as ExtensionType, properties as ExtensionProperties);
    return fromPromise(registerExtensionCreatorP(extension, this.store.dispatch));
  }

  /**
   * render application, and return an Observable . The promise is fullfied when app
   * has been rendered
   */
  public render() :Observable<Application>{
    let observe = fromPromise(this.renderPromise());
    return observe;
  }
  /**
  * render application, and return an Observable
  */
  public renderPromise(): Promise<Application> {
    this.container.render();

    this.bus.bindToStore(this.store, this.uid);
    var my = this;

    const promiseRender = new Promise<Application>((resolve, reject) => {
      let watchInitialized = watch(this.store.getState, 'initialized');
      my.store.subscribe(watchInitialized(
        (newVal: boolean, oldVal: boolean) => {
          my.initialized = newVal;
          if (newVal) {
            resolve(my);
          }
        }

      )
      );

      initApplicationCreatorP(this._initApp, this.store.dispatch);

    });



    return promiseRender;


  } 
  /**
   * Delete an operation resultset from the store, and stop it if previoulsy launched.
   * @param serviceOperationId  Id of service
   */
  public clearServiceOperation(id:string){
    
    this.store.dispatch(clearServiceCreator({id}));

  }

  /**
   * @private
   */
  protected handleErrorsLogs() {
    const w = watch(this.store.getState, 'error');
    this.store.subscribe(w((error: any, oldVal: any, objectPath: string) => {
      console.error('ERROR IN CALLING ACTION', error);
    }));
  }
}

/**
 * Create and render an application, returning a promise fullfied when
 * application is created
 *
 *
 * ```javascript
 *
 * var options =
 *  {
 *    applicationId:"http://local/appid",
 *    elementId:"idDiv"   
 *    map:{
 *      "id":"geomap",
 *     "extent":{
 *       minX: 61,
 *        maxX: 23,
 *      minY: 2312,
 *     maxY: 12,
 *      crs: 2154,
 *     }
 *   }
 * };
 * let geoApp=null;
 *  var geoApiBuilder =geoApi.createApplication(options);
 *  geoApiBuilder.then(
 *      result=>{
 *          console.log('application created!');
 *          console.log(result);
 *           geoApp=result ;
 *          geoApp.zoom();
 *      },
 *      error=>{
 *          console.error('error on creating application');
 *          console.log(error);
 *       }
 *  );
 *
 * ```
 *
 *
 */

async function createApplication(
  options: INIT_APP): Promise<Application>;
async function createApplication(
  idElement: string, urlApplication: string, options: INIT_APP): Promise<Application>;
async function createApplication(
  firstParams: string | INIT_APP, urlApplication?: string, optionsParam?: INIT_APP): Promise<Application> {
  //old method

  let geoapp: Application;
  if (typeof firstParams === "string") {

    if (urlApplication !== undefined) {
      if (optionsParam === undefined) {
        optionsParam = {
          applicationId: urlApplication,
          elementId: firstParams,
        };
      }
      optionsParam.applicationId = urlApplication;
      optionsParam.elementId = firstParams;
    } else {
      throw new Error('createApplication function must take one or three parameters');
    }
    geoapp = new Application(optionsParam);
  } else {
    geoapp = new Application(firstParams);
  }
  const value = await geoapp.renderPromise();
  return value;
}
export { createApplication };
/**
 * Create and render an application, returning it.
 *
 * in contrary to createApplication, the object is directly returned. So you
 * have to check manually steps of it being built.
 *
 * @deprecated , see createApplicationAsync
 * 
 */
export function application(
  idElement: string, urlApplication: string, options: INIT_APP): Application {

  options.elementId = idElement;
  options.applicationId = urlApplication
  const geoapp = new Application(options);
  geoapp.render();
  return geoapp;
}


/**
 * Create and render an application, returning it.
 *
 * in contrary to createApplication, the object is directly returned. So you
 * have to check manually steps of it being built.
 *
 * ```javascript
 *
 * var options =
 *  {
 *    
 *    map:{
 *      "id":"geomap",
 *     "extent":{
 *       minX: 61,
 *        maxX: 23,
 *      minY: 2312,
 *     maxY: 12,
 *      crs: 2154,
 *     }
 *   }
 * };
 *
 *  var  geoApp =geoApi.createApplicationAsync("map","http://local",options);
 *
 * geoApp.on('initialized',function(test){
 *
 *      console.log('app launched');
 *
 * });
 * ```
 *
 * 
 */
export function createApplicationAsync(idElement: string, urlApplication: string, options: INIT_APP): Application;
export function createApplicationAsync(firstParams: string | INIT_APP, urlApplication?: string, optionsParam?: INIT_APP): Application {

  let geoapp: Application;
  if (typeof firstParams === "string") {
    if (urlApplication !== undefined) {
      if (optionsParam === undefined) {
        optionsParam = {
          applicationId: urlApplication,
          elementId: firstParams,
        };
      }
      optionsParam.applicationId = urlApplication;
      optionsParam.elementId = firstParams;
    } else {
      throw new Error('createApplication function must take one or three parameters');
    }
    geoapp = new Application(optionsParam);
  } else {
    geoapp = new Application(firstParams);
  }

  geoapp.render();
  return geoapp;
}
